import { Stack } from "@mui/joy";
import { ProgramContentType } from "data/BlockComponent";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopTimePicker } from "@mui/x-date-pickers/DesktopTimePicker";
import dayjs, { Dayjs } from "dayjs";
import { setProgramContentData } from "logic/logics";
import { theme } from "styles/theme";
import { useState } from "react"; // useState import

export default function TimeSelectCell({
  defaultValue,
  blockIndex,
  setData,
  lineIndex,
  cellIndex,
  editorKey,
  timeStepOption,
}: {
  defaultValue?: string;
  blockIndex: number;
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  lineIndex: number;
  cellIndex: number;
  editorKey?: string;
  timeStepOption?: "hour" | "quarter" | "minute";
}) {
  const [open, setOpen] = useState(false); // 팝업 열림/닫힘 상태

  const today = dayjs().format("YYYY-MM-DD");
  const initialValue = `${today}T${defaultValue}`;

  const option = timeStepOption ?? "hour";
  const timeStepsProp =
    option === "hour"
      ? { hours: 1, minutes: 60, seconds: 5 }
      : option === "quarter"
        ? { hours: 1, minutes: 15, seconds: 5 }
        : { hours: 1, minutes: 1, seconds: 5 };

  const handleChange = (value?: Dayjs) => {
    setProgramContentData({
      setData,
      blockIndex,
      lineIndex,
      cellIndex,
      newlyAddedData: { value: dayjs(value).format("HH:mm") },
    });
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={"ko"}
      localeText={{ okButtonLabel: "확인" }}
    >
      <Stack>
        <DesktopTimePicker
          key={`${editorKey}`}
          name="startTime"
          defaultValue={dayjs(initialValue)}
          onChange={(newValue) => {
            if (newValue) handleChange(newValue);
          }}
          // 팝업 상태를 제어하기 위한 props 추가
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          slotProps={{
            textField: {
              // TextField를 클릭하면 팝업이 열리도록 onClick 핸들러 추가
              onClick: () => setOpen(true),
              inputProps: { "data-disable-autofocus": true, readOnly: true },
              sx: {
                // 전체 필드를 클릭할 수 있는 것처럼 보이도록 커서 변경
                cursor: "pointer",
                // 실제 input 요소의 텍스트 선택(드래그)을 방지
                "& .MuiInputBase-input": {
                  userSelect: "none",
                },
                border: "0px",
                minHeight: 0,
                backgroundColor: "white",
                borderRadius: "6px",
                height: "48px",
                "& .MuiInputBase-root": {
                  height: "100%",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.vars.palette.divider,
                },
                "& .MuiOutlinedInput-root": {
                  "&:hover fieldset": {
                    borderColor: theme.vars.palette.divider,
                  },
                  "&.Mui-focused fieldset": {
                    borderWidth: "1.4px",
                    borderColor: theme.vars.palette.primary.solid,
                  },
                },
              },
              onKeyDown: (e) => {
                e.preventDefault();
              },
              onPaste: (e) => {
                e.preventDefault();
              },
            },
            desktopPaper: {
              sx: {
                maxHeight: 320,
                p: 0,
              },
            },
            layout: {
              sx: {
                maxHeight: "100%",
                overflow: "hidden",
                p: 0,
                gap: 0,
                "& .MuiPickersShortcuts-root": {
                  display: "none",
                },
                "& .MuiPickersLayout-contentWrapper": {
                  p: 0,
                  maxHeight: 280,
                  overflow: "hidden",
                },
                "& .MuiMultiSectionDigitalClock-root, & .MuiDigitalClock-root":
                  {
                    px: 0,
                    maxHeight: 240,
                    overflow: "hidden",
                    columnGap: "4px",
                  },
                "& .MuiMultiSectionDigitalClock-root .MuiList-root, & .MuiDigitalClock-root .MuiList-root":
                  {
                    maxHeight: 240,
                    overflow: "auto",
                    p: 0,
                    m: 0,
                    overscrollBehavior: "contain",
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": {
                      display: "none",
                    },
                    "&::before, &::after": {
                      display: "none",
                    },
                    "& .MuiMenuItem-root": {
                      minHeight: 28,
                      p: 1,
                    },
                  },
                "& .MuiPickersLayout-actionBar": {
                  m: 0,
                  p: 0.5,
                },
              },
            },
          }}
          timeSteps={timeStepsProp}
        />
      </Stack>
    </LocalizationProvider>
  );
}
